import type { HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { debugLog } from '../../helpers/debugLog';
import { type OfflineSnapshot, OfflineBookRegistry, type OutboxOp } from './offline-db';
import { OfflineOutbox } from './offline-outbox';
import { OfflineSessionService } from './offline-session';
import {
  applyWrite,
  newTempId,
  OFFLINE_READINGS_URL,
  type OfflineWriteRoute,
  planEnqueue,
  readingBody,
  serializeBody,
  syntheticResponse,
} from './offline-writes';

/**
 * Keeps the downloaded copy of the reader data up to date. Offline, a write is queued and
 * answered locally; online (book downloaded), the server response is applied so the next offline
 * open shows the latest highlights, position, settings and activities.
 */
@Injectable({
  providedIn: 'root',
})
export class OfflineStore {
  private readonly registry = inject(OfflineBookRegistry);
  private readonly outbox = inject(OfflineOutbox);
  private readonly offlineSession = inject(OfflineSessionService);

  /** Snapshot updates run one after another: each one starts from the previous result. */
  private chain: Promise<unknown> = Promise.resolve();

  async recordOffline(request: HttpRequest<unknown>, route: OfflineWriteRoute): Promise<unknown> {
    return this.serialize(async () => {
      const book = this.offlineSession.book();
      const snapshot = this.offlineSession.snapshot();
      if (!book || !snapshot) throw new Error('No offline book open');
      const now = Date.now();
      const { body, payload } = serializeBody(OfflineStore.withReadDate(request.body, route, now));
      const tempId =
        route.action === 'create' && typeof payload['id'] !== 'string' ? newTempId() : undefined;
      const response = syntheticResponse(route, payload, {
        tempId,
        snapshot,
        metadata: book.metadata,
        now,
      });
      if (route.kind === 'reading') {
        await this.queueReading(book.loanKey, route, { payload, tempId, now });
        return response;
      }
      const { method } = request;
      if (method !== 'POST' && method !== 'PUT' && method !== 'DELETE') {
        throw new Error(`Unexpected offline write ${method}`);
      }
      const op: OutboxOp = {
        loanKey: book.loanKey,
        opId: crypto.randomUUID(),
        method,
        url: request.urlWithParams,
        body,
        tempId,
        coalesceKey:
          route.kind === 'position' || route.kind === 'settings' ? route.kind : undefined,
        createdAt: now,
      };
      await this.outbox.apply(planEnqueue(await this.outbox.pending(book.loanKey), op, route));
      await this.save(applyWrite(snapshot, { route, payload, response, now }));
      return response;
    });
  }

  recordOnline(
    loanKey: string,
    write: { request: HttpRequest<unknown>; route: OfflineWriteRoute; response: unknown },
  ): void {
    const { request, route, response } = write;
    void this.serialize(async () => {
      const snapshot = await this.registry.getSnapshot(loanKey);
      if (!snapshot) return;
      const { payload } = serializeBody(request.body);
      await this.registry.putSnapshot(
        applyWrite(snapshot, { route, payload, response, now: Date.now() }),
      );
    }).catch((error: unknown) => {
      debugLog('Could not update the offline copy', error);
    });
  }

  /** Highlights and bookmarks as the server has them, for the next offline open. */
  recordOnlineSelections(loanKey: string, selections: OfflineSnapshot['selections']): void {
    void this.serialize(async () => {
      const snapshot = await this.registry.getSnapshot(loanKey);
      if (snapshot) await this.registry.putSnapshot({ ...snapshot, selections });
    }).catch((error: unknown) => {
      debugLog('Could not update the offline copy', error);
    });
  }

  /** Reading sessions are not part of the copy: they are only queued for the stats service. */
  private async queueReading(
    loanKey: string,
    route: OfflineWriteRoute,
    reading: { payload: Record<string, unknown>; tempId?: string; now: number },
  ): Promise<void> {
    const { payload, tempId, now } = reading;
    const pending = await this.outbox.pending(loanKey);
    const body = readingBody(route, payload, { pending, now });
    if (!body) return;
    const op: OutboxOp = {
      loanKey,
      opId: crypto.randomUUID(),
      method: 'POST',
      url: OFFLINE_READINGS_URL,
      body,
      tempId,
      createdAt: now,
    };
    await this.outbox.apply(planEnqueue(pending, op, route));
  }

  private async save(snapshot: OfflineSnapshot): Promise<void> {
    await this.registry.putSnapshot(snapshot);
    this.offlineSession.updateSnapshot(snapshot);
  }

  private async serialize<T>(task: () => Promise<T>): Promise<T> {
    const result = this.chain.then(task);
    this.chain = result.catch(() => undefined);
    return result;
  }

  /** An offline position carries when it was read, so the server can tell which is newer. */
  private static withReadDate(body: unknown, route: OfflineWriteRoute, now: number): unknown {
    if (route.kind !== 'position' || typeof body !== 'object' || body === null) return body;
    const { savedPlace } = body as { savedPlace?: object };
    return { ...body, savedPlace: { ...savedPlace, dateLastRead: now } };
  }
}
